// Mein Tag (Kalorien-Uebersicht, 0.10.0). Privat pro Person: alle Schluessel haengen an der Nutzer-ID, der Server
// liefert nur die eigenen Eintraege (RLS). Offline angelegte Eintraege tragen ihre Geraete-ID und warten in
// localStorage (je Person), bis sie nachgetragen sind, wie die eigenen Eintraege der Einkaufsliste (w10).
import { useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiFehler } from './client';
import { istNetzfehler, neueId } from './offline';
import { useNutzerId } from './rezepte';
import { zuIso } from '../lib/datum';
import type { components } from './schema.gen';

type S = components['schemas'];
export type KalorienProfil = S['KalorienProfil'];
export type EssensEintrag = S['EssensEintrag'];
export type EssensTeil = S['EssensTeil'];
export type EintragEingabe = S['EintragEingabe'];
export type MeinTagDaten = S['Tag'];
export type Geplant = S['Geplant'];
export type Vorlage = S['Vorlage'];
export type RezeptWahl = S['RezeptWahl'];
export type RezeptAuswahl = S['RezeptAuswahl'];
export type BarcodeErgebnis = S['BarcodeErgebnis'];
export type Produkt = S['Produkt'];
export type ProduktEingabe = S['ProduktEingabe'];
export type Schaetzung = S['Schaetzung'];
export type Mahlzeit = EintragEingabe['mahlzeit'];
export type Herkunft = EintragEingabe['herkunft'];

export const MAHLZEITEN: readonly Mahlzeit[] = ['fruehstueck', 'mittag', 'abend', 'snacks'];
export const MAHLZEIT_NAME: Record<Mahlzeit, string> = { fruehstueck: 'Frühstück', mittag: 'Mittag', abend: 'Abend', snacks: 'Snacks' };
/** "Zum Frühstück eintragen", "Snack eintragen" (R7-MeinTag-Gefuellt) */
export const MAHLZEIT_EINTRAGEN: Record<Mahlzeit, string> = {
  fruehstueck: 'Zum Frühstück eintragen',
  mittag: 'Zum Mittag eintragen',
  abend: 'Zum Abend eintragen',
  snacks: 'Snack eintragen',
};
/** Herkunfts-Badge, vier Woerter (GL-023 §9.15). "eigen" hat kein Badge. */
export const HERKUNFT_WORT: Record<Herkunft, string | null> = {
  rezept: 'aus Rezept',
  packung: 'Packung',
  berechnet: 'berechnet',
  geschaetzt: 'geschätzt',
  eigen: null,
};

/** Heute auf dem Geraet (Tagesgrenze Mitternacht, Zeitzone des Geraets, Annahme 3). */
export function heuteLokal(): string {
  return zuIso(new Date());
}

/** Vorgewaehlte Mahlzeit nach Uhrzeit: bis 10:30 Fruehstueck, bis 15:00 Mittag, bis 17:00 Snacks, danach Abend. */
export function mahlzeitJetzt(d = new Date()): Mahlzeit {
  const m = d.getHours() * 60 + d.getMinutes();
  if (m < 10 * 60 + 30) return 'fruehstueck';
  if (m < 15 * 60) return 'mittag';
  if (m < 17 * 60) return 'snacks';
  return 'abend';
}

export function istMahlzeit(x: string | null): x is Mahlzeit {
  return x !== null && (MAHLZEITEN as readonly string[]).includes(x);
}

/** 1240 -> "1.240" */
export function zahl(n: number): string {
  return Math.round(n).toLocaleString('de-DE');
}

/** 0.5 -> "½", 1.5 -> "1½", sonst "0,75" */
export function portionText(p: number): string {
  const ganz = Math.floor(p);
  const rest = Math.round((p - ganz) * 100) / 100;
  if (rest === 0.5) return ganz === 0 ? '½' : `${ganz}½`;
  return p.toLocaleString('de-DE', { maximumFractionDigits: 2 });
}

export function portionenSatz(p: number): string {
  return `${portionText(p)} ${p === 1 ? 'Portion' : 'Portionen'}`;
}

export const meinTagSchluessel = {
  alles: (n: string) => ['nutzer', n, 'meintag'] as const,
  profil: (n: string) => ['nutzer', n, 'meintag', 'profil'] as const,
  tag: (n: string, d: string) => ['nutzer', n, 'meintag', 'tag', d] as const,
  zuletzt: (n: string) => ['nutzer', n, 'meintag', 'zuletzt'] as const,
  rezepte: (n: string) => ['nutzer', n, 'meintag', 'rezepte'] as const,
};

export function useKalorienProfil() {
  const n = useNutzerId();
  return useQuery({
    queryKey: meinTagSchluessel.profil(n),
    queryFn: () => api<KalorienProfil>('/mein-tag/profil'),
    enabled: n !== 'abgemeldet',
    staleTime: 5 * 60_000,
  });
}

/** Ist die Uebersicht fuer diese Person an? Solange unbekannt: nein (Tab und Kacheln erscheinen erst danach). */
export function useMeinTagAn(): boolean {
  return useKalorienProfil().data?.aktiv === true;
}

export function useProfilAendern() {
  const n = useNutzerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { aktiv?: boolean; tagesziel_kcal?: number }) =>
      api<KalorienProfil>('/mein-tag/profil', { method: 'PUT', body: JSON.stringify(a) }),
    onSuccess: (p) => {
      qc.setQueryData(meinTagSchluessel.profil(n), p);
      void qc.invalidateQueries({ queryKey: meinTagSchluessel.alles(n) });
    },
  });
}

export function useAlleLoeschen() {
  const n = useNutzerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ geloescht: number }>('/mein-tag/eintraege', { method: 'DELETE' }),
    onSuccess: () => {
      warteschlangeSchreiben(n, []);
      void qc.invalidateQueries({ queryKey: meinTagSchluessel.alles(n) });
    },
  });
}

// ---------------------------------------------------------------- Warteschlange (offline angelegt)

const wSchluessel = (n: string) => `nebenbei2-meintag:${n}`;
export const MEINTAG_EREIGNIS = 'nebenbei2-meintag';
/** "Nochmal" im Toast: sofort nachtragen statt auf den 10-s-Takt zu warten */
export const NACHTRAGEN_EREIGNIS = 'nebenbei2-meintag-nachtragen';
export function jetztNachtragen(): void {
  window.dispatchEvent(new Event(NACHTRAGEN_EREIGNIS));
}

function istEingabe(x: unknown): x is EintragEingabe & { id: string } {
  if (typeof x !== 'object' || x === null) return false;
  const e = x as Record<string, unknown>;
  return typeof e.id === 'string' && typeof e.datum === 'string' && typeof e.name === 'string' && typeof e.kcal === 'number';
}

export function warteschlange(n: string): Array<EintragEingabe & { id: string }> {
  try {
    const roh: unknown = JSON.parse(localStorage.getItem(wSchluessel(n)) ?? '[]');
    return Array.isArray(roh) ? roh.filter(istEingabe) : [];
  } catch {
    return [];
  }
}

function warteschlangeSchreiben(n: string, liste: ReadonlyArray<EintragEingabe & { id: string }>): void {
  if (liste.length === 0) localStorage.removeItem(wSchluessel(n));
  else localStorage.setItem(wSchluessel(n), JSON.stringify(liste));
  window.dispatchEvent(new Event(MEINTAG_EREIGNIS));
}

function vormerken(n: string, e: EintragEingabe & { id: string }): void {
  warteschlangeSchreiben(n, [...warteschlange(n).filter((x) => x.id !== e.id), e]);
}

function erledigt(n: string, id: string): void {
  warteschlangeSchreiben(n, warteschlange(n).filter((x) => x.id !== id));
}

/** Wartende Eintraege in den Tag einrechnen, damit sie sofort sichtbar sind (Badge und Summe stimmen). */
function mitWartenden(t: MeinTagDaten, wartend: ReadonlyArray<EintragEingabe & { id: string }>): MeinTagDaten {
  const heute = wartend.filter((w) => w.datum === t.datum);
  if (heute.length === 0) return t;
  const vorhanden = new Set(t.mahlzeiten.flatMap((m) => m.eintraege.map((e) => e.id)));
  const neu: EssensEintrag[] = heute
    .filter((w) => !vorhanden.has(w.id))
    .map((w) => ({
      id: w.id,
      datum: w.datum,
      mahlzeit: w.mahlzeit,
      name: w.name,
      menge_text: w.menge_text ?? null,
      menge_g: w.menge_g ?? null,
      portionen: w.portionen ?? null,
      kcal: w.kcal,
      eiweiss_g: w.eiweiss_g ?? null,
      kohlenhydrate_g: w.kohlenhydrate_g ?? null,
      fett_g: w.fett_g ?? null,
      ballaststoffe_g: w.ballaststoffe_g ?? null,
      herkunft: w.herkunft,
      rezept_id: w.rezept_id ?? null,
      rezept_slug: null,
      bild: null,
      kategorie: null,
      barcode: w.barcode ?? null,
      bestandteile: w.bestandteile ?? null,
      erstellt_am: new Date().toISOString(),
    }));
  if (neu.length === 0) return t;
  const mahlzeiten = t.mahlzeiten.map((m) => {
    const dazu = neu.filter((e) => e.mahlzeit === m.mahlzeit);
    return { ...m, eintraege: [...m.eintraege, ...dazu], kcal: m.kcal + dazu.reduce((s, e) => s + e.kcal, 0) };
  });
  const plus = neu.reduce((s, e) => s + e.kcal, 0);
  return {
    ...t,
    mahlzeiten,
    kcal: t.kcal + plus,
    sieben_tage: t.sieben_tage.map((x) => (x.datum === t.datum ? { ...x, kcal: (x.kcal ?? 0) + plus } : x)),
  };
}

export function useMeinTag(datum: string) {
  const n = useNutzerId();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: meinTagSchluessel.tag(n, datum),
    queryFn: () => api<MeinTagDaten>(`/mein-tag/tag?datum=${encodeURIComponent(datum)}`),
    enabled: n !== 'abgemeldet',
    staleTime: 30_000,
    select: (t: MeinTagDaten) => mitWartenden(t, warteschlange(n)),
  });
  // Neu zeichnen, wenn sich die Warteschlange aendert (offline angelegt, nachgetragen)
  useEffect(() => {
    const neu = () => void qc.invalidateQueries({ queryKey: meinTagSchluessel.tag(n, datum) });
    window.addEventListener(MEINTAG_EREIGNIS, neu);
    return () => window.removeEventListener(MEINTAG_EREIGNIS, neu);
  }, [qc, n, datum]);
  return q;
}

export function useZuletzt(an = true) {
  const n = useNutzerId();
  return useQuery({
    queryKey: meinTagSchluessel.zuletzt(n),
    queryFn: () => api<Vorlage[]>('/mein-tag/zuletzt'),
    enabled: n !== 'abgemeldet' && an,
    staleTime: 60_000,
  });
}

export function useMeinTagRezepte() {
  const n = useNutzerId();
  return useQuery({
    queryKey: meinTagSchluessel.rezepte(n),
    queryFn: () => api<RezeptAuswahl>('/mein-tag/rezepte'),
    enabled: n !== 'abgemeldet',
    staleTime: 60_000,
  });
}

export type SpeicherErgebnis = { art: 'gespeichert'; eintrag: EssensEintrag } | { art: 'wartet'; id: string };

/** Anlegen oder aendern. Ohne Netz bleibt der Eintrag auf dem Geraet und wird nachgetragen. */
export function useEintragSpeichern() {
  const n = useNutzerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ eingabe, neu }: { eingabe: EintragEingabe; neu: boolean }): Promise<SpeicherErgebnis> => {
      const id = eingabe.id ?? neueId();
      const mitId = { ...eingabe, id };
      try {
        const e = neu
          ? await api<EssensEintrag>('/mein-tag/eintraege', { method: 'POST', body: JSON.stringify(mitId) })
          : await api<EssensEintrag>(`/mein-tag/eintraege/${id}`, { method: 'PUT', body: JSON.stringify(mitId) });
        erledigt(n, id);
        return { art: 'gespeichert', eintrag: e };
      } catch (f) {
        if (neu && (istNetzfehler(f) || (f instanceof ApiFehler && f.status >= 500))) {
          vormerken(n, mitId);
          return { art: 'wartet', id };
        }
        throw f;
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: meinTagSchluessel.alles(n) });
    },
  });
}

export function useEintragLoeschen() {
  const n = useNutzerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (warteschlange(n).some((w) => w.id === id)) {
        erledigt(n, id);
        return;
      }
      await api<null>(`/mein-tag/eintraege/${id}`, { method: 'DELETE' });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: meinTagSchluessel.alles(n) });
    },
  });
}

/** Wartende Eintraege nachtragen: beim Start, wenn das Netz zurueckkommt und alle 10 s. */
export function useMeinTagNachtragen() {
  const n = useNutzerId();
  const qc = useQueryClient();
  const nachtragen = useCallback(async () => {
    if (n === 'abgemeldet') return;
    const liste = warteschlange(n);
    if (liste.length === 0) return;
    let etwas = false;
    for (const e of liste) {
      try {
        await api<EssensEintrag>('/mein-tag/eintraege', { method: 'POST', body: JSON.stringify(e) });
        erledigt(n, e.id);
        etwas = true;
      } catch (f) {
        // Ein dauerhaft abgelehnter Eintrag (4xx) blockiert die Schlange nicht
        if (f instanceof ApiFehler && f.status >= 400 && f.status < 500) erledigt(n, e.id);
        else break;
      }
    }
    if (etwas) void qc.invalidateQueries({ queryKey: meinTagSchluessel.alles(n) });
  }, [n, qc]);
  useEffect(() => {
    void nachtragen();
    const t = window.setInterval(() => void nachtragen(), 10_000);
    const online = () => void nachtragen();
    window.addEventListener('online', online);
    window.addEventListener(NACHTRAGEN_EREIGNIS, online);
    return () => {
      window.clearInterval(t);
      window.removeEventListener('online', online);
      window.removeEventListener(NACHTRAGEN_EREIGNIS, online);
    };
  }, [nachtragen]);
  return nachtragen;
}

export function barcodeSuchen(code: string): Promise<BarcodeErgebnis> {
  return api<BarcodeErgebnis>(`/mein-tag/barcode/${encodeURIComponent(code)}`);
}

export function barcodeMerken(code: string, p: ProduktEingabe): Promise<Produkt> {
  return api<Produkt>(`/mein-tag/barcode/${encodeURIComponent(code)}`, { method: 'PUT', body: JSON.stringify(p) });
}

export function schaetzen(textEingabe: string): Promise<Schaetzung> {
  return api<Schaetzung>('/mein-tag/schaetzen', { method: 'POST', body: JSON.stringify({ text: textEingabe }) });
}
